import { useState, useRef } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { importAPI, advertisersAPI } from '../lib/api';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, CheckCircle2, XCircle, Download, Loader2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useEffect } from 'react';

const IMPORT_TYPES = [
  { value: 'campaigns', label: 'Campaigns', description: 'Import campaign data' },
  { value: 'performance_data', label: 'Performance Data', description: 'Import metrics (impressions, clicks, spend)' },
  { value: 'advertisers', label: 'Advertisers', description: 'Bulk create advertisers' },
];

const DataImport = () => {
  const [activeTab, setActiveTab] = useState('campaigns');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [advertisers, setAdvertisers] = useState([]);
  const [selectedAdvertiser, setSelectedAdvertiser] = useState('');
  const [platform, setPlatform] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    advertisersAPI.getAll()
      .then(res => setAdvertisers(res.data || []))
      .catch(err => console.error('Failed to fetch advertisers:', err));
  }, []);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    setFile(selectedFile);
    setResult(null);

    try {
      const response = await importAPI.preview(selectedFile);
      setPreview(response.data);
      
      if (response.data.detected_type && response.data.detected_type !== activeTab) {
        toast.info(`File appears to be ${response.data.detected_type} data. You may want to switch tabs.`);
      }
    } catch (error) {
      toast.error('Failed to preview file');
      setPreview(null);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    // Require advertiser selection for performance data
    if (activeTab === 'performance_data' && (!selectedAdvertiser || selectedAdvertiser === 'none')) {
      toast.error('Please select an advertiser for performance data import');
      return;
    }

    setImporting(true);
    setResult(null);

    try {
      let response;
      const advId = selectedAdvertiser === 'none' ? null : (selectedAdvertiser || null);
      
      switch (activeTab) {
        case 'campaigns':
          response = await importAPI.importCampaigns(file, advId);
          break;
        case 'performance_data':
          response = await importAPI.importPerformanceData(file, platform || null, advId);
          break;
        case 'advertisers':
          response = await importAPI.importAdvertisers(file);
          break;
        default:
          throw new Error('Unknown import type');
      }

      setResult(response.data);
      
      if (response.data.successful > 0) {
        toast.success(`Successfully imported ${response.data.successful} records`);
      }
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} records failed to import`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await importAPI.getTemplate(activeTab);
      const { headers, sample } = response.data;
      
      const csvContent = [headers.join(','), sample.join(',')].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `${activeTab}_template.csv`;
      a.click();
      
      URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const resetForm = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fadeIn">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-900">Data Import</h1>
            <p className="text-sm text-slate-500 mt-1">
              Import campaigns, performance data, and advertisers via CSV
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Import Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="p-0">
                <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); resetForm(); }}>
                  <div className="border-b px-4">
                    <TabsList className="bg-transparent">
                      {IMPORT_TYPES.map(type => (
                        <TabsTrigger
                          key={type.value}
                          value={type.value}
                          className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-slate-900 rounded-none"
                        >
                          {type.label}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </div>

                  {IMPORT_TYPES.map(type => (
                    <TabsContent key={type.value} value={type.value} className="p-6 space-y-6">
                      <p className="text-sm text-slate-500">{type.description}</p>
                      
                      {/* File Upload */}
                      <div
                        className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center cursor-pointer hover:border-slate-400 transition-colors"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".csv"
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                        <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                        {file ? (
                          <div>
                            <p className="font-medium text-slate-900">{file.name}</p>
                            <p className="text-sm text-slate-500 mt-1">
                              {preview?.total_rows || 0} rows detected
                            </p>
                          </div>
                        ) : (
                          <div>
                            <p className="text-slate-600">Click to select a CSV file</p>
                            <p className="text-sm text-slate-400 mt-1">or drag and drop</p>
                          </div>
                        )}
                      </div>

                      {/* Additional Options */}
                      {(activeTab === 'campaigns' || activeTab === 'performance_data') && (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="flex items-center gap-1">
                              Advertiser
                              {activeTab === 'performance_data' && (
                                <span className="text-red-500">*</span>
                              )}
                              {activeTab === 'campaigns' && (
                                <span className="text-slate-400 text-xs font-normal">(Optional)</span>
                              )}
                            </Label>
                            <Select value={selectedAdvertiser} onValueChange={setSelectedAdvertiser}>
                              <SelectTrigger className={activeTab === 'performance_data' && !selectedAdvertiser ? 'border-amber-400' : ''}>
                                <SelectValue placeholder="Select advertiser" />
                              </SelectTrigger>
                              <SelectContent>
                                {activeTab === 'campaigns' && (
                                  <SelectItem value="none">None</SelectItem>
                                )}
                                {advertisers.map(adv => (
                                  <SelectItem key={adv.id} value={adv.id}>
                                    {adv.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {activeTab === 'performance_data' && !selectedAdvertiser && (
                              <p className="text-xs text-amber-600 mt-1">
                                Required for data isolation
                              </p>
                            )}
                          </div>
                          
                          {activeTab === 'performance_data' && (
                            <div>
                              <Label>Platform (Optional)</Label>
                              <Input
                                value={platform}
                                onChange={(e) => setPlatform(e.target.value)}
                                placeholder="e.g., Meta, DV360"
                              />
                            </div>
                          )}
                        </div>
                      )}

                      {/* Preview Table */}
                      {preview && preview.sample_rows.length > 0 && (
                        <div>
                          <h3 className="font-medium text-slate-900 mb-2">Preview (first 5 rows)</h3>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b">
                                  {preview.headers.slice(0, 6).map((header, i) => (
                                    <th key={i} className="text-left p-2 font-medium text-slate-600">
                                      {header}
                                    </th>
                                  ))}
                                  {preview.headers.length > 6 && (
                                    <th className="text-left p-2 font-medium text-slate-400">
                                      +{preview.headers.length - 6} more
                                    </th>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {preview.sample_rows.map((row, i) => (
                                  <tr key={i} className="border-b">
                                    {preview.headers.slice(0, 6).map((header, j) => (
                                      <td key={j} className="p-2 text-slate-700 truncate max-w-[150px]">
                                        {row[header] || '-'}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex items-center justify-between pt-4 border-t">
                        <Button variant="outline" onClick={downloadTemplate}>
                          <Download className="w-4 h-4 mr-2" />
                          Download Template
                        </Button>
                        
                        <div className="flex gap-2">
                          {file && (
                            <Button variant="outline" onClick={resetForm}>
                              Clear
                            </Button>
                          )}
                          <Button
                            onClick={handleImport}
                            disabled={!file || importing}
                          >
                            {importing ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Importing...
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4 mr-2" />
                                Import Data
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {/* Import Results */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                  Import Results
                </CardTitle>
              </CardHeader>
              <CardContent>
                {result ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <div className="text-2xl font-bold text-slate-900">{result.total_rows}</div>
                        <div className="text-xs text-slate-500">Total</div>
                      </div>
                      <div className="p-3 bg-emerald-50 rounded-lg">
                        <div className="text-2xl font-bold text-emerald-600">{result.successful}</div>
                        <div className="text-xs text-emerald-600">Success</div>
                      </div>
                      <div className="p-3 bg-red-50 rounded-lg">
                        <div className="text-2xl font-bold text-red-600">{result.failed}</div>
                        <div className="text-xs text-red-600">Failed</div>
                      </div>
                    </div>

                    {result.errors && result.errors.length > 0 && (
                      <div>
                        <h4 className="font-medium text-slate-900 mb-2">Errors</h4>
                        <div className="max-h-64 overflow-y-auto space-y-2">
                          {result.errors.slice(0, 10).map((err, i) => (
                            <div key={i} className="p-2 bg-red-50 rounded text-sm">
                              <span className="font-medium text-red-700">Row {err.row}:</span>
                              <span className="text-red-600 ml-1">{err.error}</span>
                            </div>
                          ))}
                          {result.errors.length > 10 && (
                            <p className="text-sm text-slate-500 text-center">
                              +{result.errors.length - 10} more errors
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-400">
                    <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Import results will appear here</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Help Card */}
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                  CSV Format Tips
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-slate-600 space-y-2">
                <p>• First row must contain column headers</p>
                <p>• Use UTF-8 encoding for special characters</p>
                <p>• Dates should be in YYYY-MM-DD format</p>
                <p>• Numbers should not contain currency symbols</p>
                <p>• Download the template for the correct format</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DataImport;
